/**
 * ComboSelect — filterable dropdown component.
 *
 * Replaces native `<select>` throughout the codebase with a consistent
 * input + dropdown combo that supports:
 *   - Typing to filter options
 *   - Click to select / clear
 *   - Keyboard navigation (Enter to select first match, Escape to close)
 *   - Optional grouping via `group` field on options
 *   - Works without any icon library (inline SVGs)
 */

import {
    type FC,
    type CSSProperties,
    type ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { usePluginSdkT } from "../i18n";
import { usePluginSurface } from "./PluginSurfaceContext";

/* ── Inline SVG icons (no external dependency) ── */

const ChevronDownIcon = ({ size = 12 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const XIcon = ({ size = 12 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
    </svg>
);

/* ── Types ── */

export interface ComboSelectOption {
    /** Value stored in config */
    value: string;
    /** Display label */
    label: string;
    /** Optional group name (renders as a group header) */
    group?: string;
    /** Optional icon/emoji prefix (string or ReactNode) */
    icon?: ReactNode;
    /** Optional trailing metadata rendered on the right side of an option row */
    meta?: ReactNode;
}

export interface ComboSelectProps {
    /** Current selected value */
    value: string;
    /** Called when value changes */
    onChange: (value: string) => void;
    /** Available options */
    options: ComboSelectOption[];
    /** Placeholder when nothing is selected */
    placeholder?: string;
    /** Whether clearing (selecting empty) is allowed */
    allowClear?: boolean;
    /** Optional class name override for the container */
    className?: string;
    /** Optional label rendered above the combobox */
    label?: string | ReactNode;
    /** Disable the component */
    disabled?: boolean;
    /** Visual size — "inherit" follows surrounding inline text; "xs" is compact fixed-size; "sm" for ConfigPanel; "md" for app forms */
    size?: "inherit" | "xs" | "sm" | "md";
    /** Optional root style, useful when a renderer computes an intrinsic control width */
    style?: CSSProperties;
    /** Optional dropdown width override; defaults to the trigger width */
    dropdownWidth?: CSSProperties["width"];
    /** Optional dropdown min-width override */
    dropdownMinWidth?: CSSProperties["minWidth"];
    /** Optional dropdown max-width override */
    dropdownMaxWidth?: CSSProperties["maxWidth"];
    /** Render wrapper shells as inline spans so the control can live inside inline preview content */
    inline?: boolean;
    /** Remove trigger border/background so a parent composite field can own the visual shell */
    variant?: "default" | "embedded";
}

/* ── Component ── */

export const ComboSelect: FC<ComboSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    allowClear = true,
    className,
    label,
    disabled,
    size = "md",
    style,
    dropdownWidth,
    dropdownMinWidth,
    dropdownMaxWidth,
    inline = false,
    variant = "default",
}) => {
    const t = usePluginSdkT();
    const pluginSurface = usePluginSurface();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapperRef = useRef<HTMLElement | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Size tokens — keep visual rhythm consistent with surrounding inputs.
    // "inherit" is for inline document text where the control should follow the paragraph font.
    // "xs" matches fixed-size compact controls (h-5 + text-[11px]);
    // "sm" matches plugin ConfigPanel compact inputs (h-7 + text-[11px]);
    // "md" is the default 36px for app forms / settings center.
    const sizing = size === "inherit"
        ? {
            trigger: "min-h-[1.55em] px-[0.45em] py-0 text-[1em] leading-[1.2] font-[inherit]",
            input: "text-[1em] leading-[1.2] font-[inherit]",
            option: "px-2 py-1 text-[1em] leading-[1.35] font-[inherit]",
            groupLabel: "px-2 py-1 text-[0.75em]",
            empty: "px-2 py-1.5 text-[1em]",
            iconSize: 10,
            checkSize: "text-[0.8em]",
        }
        : size === "xs"
        ? {
            trigger: "min-h-5 px-1.5 py-0 text-[11px] leading-4",
            input: "text-[11px] leading-4",
            option: "px-2 py-1 text-[11px] leading-4",
            groupLabel: "px-2 py-1 text-[10px]",
            empty: "px-2 py-1.5 text-[11px]",
            iconSize: 10,
            checkSize: "text-[10px]",
        }
        : size === "sm"
        ? {
            trigger: "min-h-7 px-2 py-1 text-[11px] leading-4",
            input: "text-[11px] leading-4",
            option: "px-2 py-1 text-[11px] leading-4",
            groupLabel: "px-2 py-1 text-[10px]",
            empty: "px-2 py-1.5 text-[11px]",
            iconSize: 11,
            checkSize: "text-[10px]",
        }
        : {
            trigger: "min-h-9 px-3 py-2 text-[13px] leading-5",
            input: "text-[13px] leading-5",
            option: "px-3 py-2 text-[13px] leading-5",
            groupLabel: "px-3 py-1.5 text-[11px]",
            empty: "px-3 py-2.5 text-[11px]",
            iconSize: 12,
            checkSize: "text-[11px]",
        };

    // Find selected option for display
    const selected = useMemo(
        () => options.find((o) => o.value === value),
        [options, value],
    );

    // Filter options by query
    const filtered = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q) ||
                (typeof o.meta === "string" && o.meta.toLowerCase().includes(q)),
        );
    }, [options, query]);

    // Group filtered options
    const grouped = useMemo(() => {
        const hasGroups = filtered.some((o) => o.group);
        if (!hasGroups) return null;

        const groups = new Map<string, ComboSelectOption[]>();
        const ungrouped: ComboSelectOption[] = [];
        for (const o of filtered) {
            if (o.group) {
                const list = groups.get(o.group) ?? [];
                list.push(o);
                groups.set(o.group, list);
            } else {
                ungrouped.push(o);
            }
        }
        return { groups, ungrouped };
    }, [filtered]);

    const selectItem = useCallback(
        (v: string) => {
            onChange(v);
            setOpen(false);
            setQuery("");
            inputRef.current?.blur();
        },
        [onChange],
    );

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onChange("");
            setQuery("");
        },
        [onChange],
    );

    // Render a single option row
    const renderOption = (o: ComboSelectOption) => {
        const isSelected = o.value === value;
        return (
            <div
                key={o.value}
                className={`flex items-center gap-2 ${sizing.option} cursor-pointer transition-colors hover:bg-muted/60 ${isSelected ? "bg-muted/40 font-medium" : ""
                    }`}
                onMouseDown={(e) => {
                    e.preventDefault();
                    selectItem(o.value);
                }}
            >
                {o.icon && <span className="shrink-0">{o.icon}</span>}
                <span className="flex-1 truncate">{o.label}</span>
                {o.meta && (
                    <span className="max-w-[45%] shrink-0 truncate text-muted-foreground">
                        {o.meta}
                    </span>
                )}
                {isSelected && (
                    <span className={`text-primary ${sizing.checkSize} shrink-0`}>✓</span>
                )}
            </div>
        );
    };

    // Display text when closed (label only — icon renders separately)
    const displayText = selected ? selected.label : "";
    const resolvedPlaceholder = placeholder ?? t("comboSelect.placeholder");

    /* ── Portal dropdown positioning ── */
    const triggerRef = useRef<HTMLElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{
        top: number;
        left: number;
        width: number;
        direction: "down" | "up";
    } | null>(null);

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const ownerWindow = el.ownerDocument.defaultView;
        const spaceBelow = (ownerWindow?.innerHeight ?? window.innerHeight) - rect.bottom;
        const spaceAbove = rect.top;
        const maxH = 192; // max-h-48 = 12rem = 192px
        const openUp = spaceBelow < maxH && spaceAbove > spaceBelow;
        setPos({
            top: openUp ? rect.top : rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            direction: openUp ? "up" : "down",
        });
    }, []);

    const setWrapperRef = useCallback((node: HTMLElement | null) => {
        wrapperRef.current = node;
    }, []);

    const setTriggerRef = useCallback((node: HTMLElement | null) => {
        triggerRef.current = node;
    }, []);

    // Recalculate on open, scroll, resize
    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        updatePosition();
        const onUpdate = () => updatePosition();
        const ownerWindow = triggerRef.current?.ownerDocument.defaultView ?? window;
        ownerWindow.addEventListener("scroll", onUpdate, true);
        ownerWindow.addEventListener("resize", onUpdate);
        return () => {
            ownerWindow.removeEventListener("scroll", onUpdate, true);
            ownerWindow.removeEventListener("resize", onUpdate);
        };
    }, [open, updatePosition]);

    // After dropdown renders, adjust upward position to account for actual height
    useLayoutEffect(() => {
        if (!open || !pos || pos.direction !== "up" || !dropdownRef.current)
            return;
        const dh = dropdownRef.current.offsetHeight;
        const triggerRect = triggerRef.current?.getBoundingClientRect();
        if (triggerRect) {
            setPos((prev) =>
                prev ? { ...prev, top: triggerRect.top - dh - 4 } : prev,
            );
        }
    }, [open, pos?.direction, filtered.length]);

    // Close on outside click — must also account for portal dropdown
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            // `event.target` is retargeted to the custom-element host when an
            // event leaves a ShadowRoot. The composed path preserves the real
            // plugin nodes and is therefore the only reliable inside/outside
            // boundary for both the trigger and its in-surface portal.
            const path = e.composedPath();
            if (
                (wrapperRef.current && path.includes(wrapperRef.current)) ||
                (dropdownRef.current && path.includes(dropdownRef.current))
            ) {
                return;
            }

            const target = path[0] as HTMLElement | undefined;
            // Ignore clicks inside dialog portals (file picker, folder picker, etc.)
            if (target?.closest?.("[data-dialog-portal]")) return;
            setOpen(false);
            setQuery("");
        };
        const ownerDocument = wrapperRef.current?.ownerDocument ?? document;
        ownerDocument.addEventListener("mousedown", handler, true);
        return () => ownerDocument.removeEventListener("mousedown", handler, true);
    }, [open]);

    /* ── Dropdown content ── */
    const dropdown = open && pos ? (
        <div
            ref={dropdownRef}
            data-comboselect-portal
            className="fixed z-[9999] max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
            style={{
                top: pos.top,
                left: pos.left,
                width: dropdownWidth ?? pos.width,
                minWidth: dropdownMinWidth ?? (dropdownWidth ? pos.width : undefined),
                maxWidth: dropdownMaxWidth,
            }}
        >
            {filtered.length === 0 ? (
                <div className={`${sizing.empty} text-muted-foreground`}>
                    {query
                        ? t("comboSelect.noMatch", { query })
                        : t("comboSelect.noOptions")}
                </div>
            ) : grouped ? (
                <>
                    {grouped.ungrouped.map(renderOption)}
                    {Array.from(grouped.groups.entries()).map(
                        ([group, items]) => (
                            <div key={group}>
                                <div className={`sticky top-0 z-10 border-b border-border bg-popover ${sizing.groupLabel} font-semibold uppercase tracking-wide text-muted-foreground`}>
                                    {group}
                                </div>
                                {items.map(renderOption)}
                            </div>
                        ),
                    )}
                </>
            ) : (
                filtered.map(renderOption)
            )}
        </div>
    ) : null;

    // Plugin overlays stay inside their ShadowRoot. App-owned uses render in
    // place and therefore never need access to the host document portal tree.
    const dropdownContent = dropdown && pluginSurface
        ? createPortal(dropdown, pluginSurface.portalRoot)
        : dropdown;

    const labelNode = label
        ? typeof label === "string"
            ? (
                <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                    {label}
                </span>
            )
            : label
        : null;

    const inputClassName = inline
        ? `flex-none bg-transparent ${sizing.input} outline-none placeholder:text-muted-foreground/50 [field-sizing:content]`
        : `min-w-0 flex-1 bg-transparent ${sizing.input} outline-none placeholder:text-muted-foreground/50`;

    const triggerContent = (
        <>
            {/* Icon prefix when closed and a selection has an icon */}
            {!open && selected?.icon && (
                <span className="shrink-0 flex items-center">{selected.icon}</span>
            )}
            <input
                ref={inputRef}
                className={inputClassName}
                value={open ? query : displayText}
                placeholder={open ? t("comboSelect.search") : resolvedPlaceholder}
                onChange={(e) => {
                    setQuery(e.target.value);
                    if (!open) setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        setOpen(false);
                        setQuery("");
                        inputRef.current?.blur();
                    } else if (
                        e.key === "Enter" &&
                        filtered.length > 0
                    ) {
                        selectItem(filtered[0].value);
                    }
                }}
                disabled={disabled}
            />
            {allowClear && value && (
                <button
                    type="button"
                    className="shrink-0 p-0.5 rounded hover:bg-muted text-muted-foreground"
                    onMouseDown={handleClear}
                    tabIndex={-1}
                >
                    <XIcon size={sizing.iconSize} />
                </button>
            )}
            <button
                type="button"
                className="shrink-0 p-0.5 text-muted-foreground"
                tabIndex={-1}
                onMouseDown={(e) => {
                    e.preventDefault();
                    if (disabled) return;
                    setOpen(!open);
                    if (!open) inputRef.current?.focus();
                }}
            >
                <ChevronDownIcon size={sizing.iconSize} />
            </button>
        </>
    );

    const embeddedTriggerSizing = size === "inherit"
        ? "min-h-[1.2em] px-0 py-0 text-[1em] leading-[1.2] font-[inherit]"
        : size === "xs"
        ? "min-h-4 px-0 py-0 text-[11px] leading-4"
        : size === "sm"
        ? "min-h-5 px-0 py-0 text-[11px] leading-4"
        : "min-h-5 px-0 py-0 text-[13px] leading-5";
    const triggerChromeClass = variant === "embedded"
        ? "border border-transparent bg-transparent"
        : `border bg-background ${open
            ? "border-primary ring-1 ring-primary/30"
            : "border-border"
        }`;
    const triggerClassName = `box-border flex ${inline ? "" : "w-full"} items-center ${variant === "embedded" ? "gap-1" : "gap-2"} rounded-md ${variant === "embedded" ? embeddedTriggerSizing : sizing.trigger} ${triggerChromeClass} ${disabled ? "opacity-50 pointer-events-none" : ""}`;

    if (inline) {
        return (
            <span className={className} style={style} ref={setWrapperRef}>
                {labelNode}
                <span className="relative inline-block align-baseline" ref={setTriggerRef}>
                    <span className={triggerClassName} data-comboselect-trigger>
                        {triggerContent}
                    </span>
                    {dropdownContent}
                </span>
            </span>
        );
    }

    return (
        <div className={className} style={style} ref={setWrapperRef}>
            {labelNode}
            <div className="relative" ref={setTriggerRef}>
                <div className={triggerClassName} data-comboselect-trigger>
                    {triggerContent}
                </div>

                {/* Dropdown rendered via portal to avoid overflow clipping */}
                {dropdownContent}
            </div>
        </div>
    );
};
